import { Avatar, Box, Checkbox, MenuItem, Select, Tooltip } from "@mui/material";
import { Link } from "@tanstack/react-router";
import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { osType } from "@/components/config-modals/router-edit-modal/constants";
import { getTabTypeBySessionType, renderClubsCell } from "@/lib/accounts-utils";
import { APP_GMT_DATE_FORMAT } from "@/lib/constants";
import { SysAccountRegularDto } from "@/shared/api";
import { MRT_OrderedColumnDef, NilObj } from "@/types/common-types";

const useAccountCommonColumns = ({ room }: { room: string }) => {
  const { t } = useTranslation();

  return useMemo<MRT_OrderedColumnDef<SysAccountRegularDto>[]>(
    () => [
      {
        id: "enabled",
        header: t("commonColumns.enabled"),
        order: 900,
        accessorFn: ({ enabled }) => enabled ?? "-",
        Cell: ({
          row: {
            original: { enabled },
          },
        }) => <Checkbox checked={enabled} disabled />,
      },
      {
        id: "name",
        header: t("commonColumns.name"),
        order: 200,
        accessorFn: (row) => row.name ?? "-",
        Cell: ({ row }) => {
          const dt = row.original.data as any;
          return (
            <div style={{ display: "inline-flex", alignItems: "center" }}>
              <Avatar
                alt={dt?.nick ?? "?"}
                src={dt?.avatar}
                sx={{ width: 24, height: 24, mr: 1 }}
              />
              <span>{row.original.name ?? "-"}</span>
            </div>
          );
        },
      },
      {
        id: "data.source",
        header: t("commonColumns.source"),
        order: 300,
        accessorFn: ({ data }) => (data as NilObj)?.source ?? "-",
      },
      {
        id: "data.cid",
        header: "Cid",
        order: 400,
        accessorFn: ({ data }) => (data as NilObj)?.cid ?? "-",
      },
      {
        id: "data.pid",
        header: t("regularAccountsTable.pid"),
        order: 399,
        accessorFn: ({ data }) => (data as NilObj)?.pid ?? "-",
      },
      {
        id: "data.aid",
        header: t("regularAccountsTable.aid"),
        order: 401,
        accessorFn: ({ data }) => (data as NilObj)?.aid ?? "-",
      },
      {
        id: "data.clubs",
        header: t("regularAccountsTable.clubs"),
        order: 601,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row: { original } }) => renderClubsCell(original.data),
      },
      {
        id: "data.alc",
        header: t("regularAccountsTable.alc"),
        order: 601,
        accessorFn: ({ data }) => (data as NilObj)?.alc ?? "-",
      },
      {
        id: "data.gold",
        header: t("regularAccountsTable.gold"),
        order: 601,
        accessorFn: ({ data }) => (data as NilObj)?.gold ?? "-",
      },
      {
        id: "data.gems",
        header: t("regularAccountsTable.gems"),
        order: 601,
        accessorFn: ({ data }) => (data as NilObj)?.gems ?? "-",
      },
      {
        id: "data.bound_gems",
        header: t("regularAccountsTable.boundGems"),
        order: 601,
        accessorFn: ({ data }) => (data as NilObj)?.bound_gems ?? "-",
      },
      {
        id: "data.game_group",
        header: t("regularAccountsTable.gameGroup"),
        order: 500,
        accessorFn: ({ data }) => (data as NilObj)?.game_group ?? "-",
      },
      {
        id: "data.os_type",
        header: t("osType"),
        order: 600,
        accessorFn: ({ data }) =>
          osType.map((i) => i.label)[(data as NilObj)?.device?.os_type as unknown as number] ??
          "-",
      },
      {
        id: "data.nick",
        header: t("regularAccountsTable.nick"),
        order: 650,
        accessorFn: ({ data }) => (data as NilObj)?.nick ?? "-",
      },
      {
        id: "data.proxy",
        header: t("regularAccountsTable.proxy"),
        order: 675,
        accessorFn: ({ data }) => (data as NilObj)?.proxy ?? "-",
      },

      {
        id: "data.status",
        header: t("commonColumns.status"),
        order: 700,
        accessorFn: ({ data }) => (data as NilObj)?.status ?? "-",
        Cell: ({ row: { original } }) => {
          const status = (original.data as NilObj)?.status as unknown;

          return status !== undefined && typeof status === "number" ? (
            <Tooltip title={t(`accountStatus.${status}`)}>
              <p>{status}</p>
            </Tooltip>
          ) : (
            "-"
          );
        },
      },
      {
        id: "data.comment",
        header: t("serviceAccountsTable.comment"),
        order: 301,
        accessorFn: ({ data }) => (data as NilObj)?.comment ?? "-",
      },
      {
        id: "created_at",
        header: t("commonColumns.createdAt"),
        order: 800,
        accessorFn: (row) => (row.created_at ? new Date(row.created_at) : 0),
        Cell: ({
          row: {
            original: { created_at },
          },
        }) => (created_at ? moment(created_at).utc(false).format(APP_GMT_DATE_FORMAT) : "-"),
      },
      {
        id: "session.id",
        header: t("commonColumns.session"),
        order: 1000,
        accessorFn: ({ data }) => ((data as any)?.session?.id ? 1 : 0),
        Cell: ({
          row: {
            original: { session },
          },
        }) =>
          session ? (
            <Link
              to="/accounts/$room/$type"
              params={{
                room: room,
                type: getTabTypeBySessionType(session.session_type),
              }}
              search={{
                session_id: session.id,
              }}
            >
              Active
            </Link>
          ) : (
            "-"
          ),
      },
    ],
    [room, t]
  );
};

export default useAccountCommonColumns;
